import { getCategoryProducts } from "@/services/elasticsearch/category";
import { guardClosedSite } from "@/lib/site-schedule/closed-guard";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { localeHandShake, ensureHandshakeCookie } from "@/services/token/handshake";
import React, { useContext, useEffect } from "react";
import CategoryHead from "@/components/new-category/head/head";
import CategoryWrapper from "@/components/new-category/wrapper";
import { handshakeCookieName } from "@/constants";
import { event_category_viewed } from "@/utils/gtm";
import { AppContext } from "@/contexts/appContexts";
import { getCategoryData } from "@/utils/category";
import { CategoryTree } from "@/services/category/categoryTree";

const CategoryPage = ({ data: categoryData, id: categoryId, device, cat: catProp }) => {
  const { categoryData: categoryTree } = useContext(AppContext);
  const cat = catProp?.id
    ? catProp
    : getCategoryData(categoryTree?.children_data, "id", categoryId);

  useEffect(() => {
    event_category_viewed(
      cat?.name || null,
      categoryId,
      categoryData?.totalProduct || null,
      cat?.category_icon || null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  return (
    <div>
      <CategoryHead id={categoryId} device={device} cat={cat} />
      <CategoryWrapper
        device={device}
        data={categoryData}
        categoryId={categoryId}
      />
    </div>
  );
};

export default CategoryPage;
export async function getServerSideProps(context) {
  const closedSiteRedirect = guardClosedSite(context);
  if (closedSiteRedirect) return closedSiteRedirect;
  const { query, locale, req, res } = context;
  const userAgent = req.headers["user-agent"] || "";
  const isMobile =
    /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/i.test(
      userAgent
    );

  const regionId = req.cookies?.region_id || 1101;
  const mhsToken = req.cookies[handshakeCookieName(locale)];
  const handshake =
    mhsToken || (await localeHandShake(locale, { req, res }))?.data?.token;
  ensureHandshakeCookie(mhsToken, { req, res });
  const { catId: slug } = query;
  const id = slug?.at(-1)?.split("-c-")?.at(1);

  if (!id) {
    return {
      notFound: true,
    };
  }
  // ES facets carry no category meta — fetch the tree in parallel for the SSR OG tags.
  const [category, treeRes] = await Promise.all([
    getCategoryProducts({
      handshake,
      categoryId: id,
      regionId,
      pageSize: 32,
      pageNo: 0,
    }),
    CategoryTree(handshake).catch((e) => {
      console.error("[category SSR] tree fetch failed:", e?.error || e?.message);
      return null;
    }),
  ]);
  // redirect to parent category if current category not have any products
  if (slug?.length > 1 && category?.products?.length === 0) {
    res.statusCode = 301;
    return {
      redirect: {
        destination: `/${locale}/${slug?.slice(0, -1)?.join("/")}`,
        permanent: false,
      },
    };
  }

  const filteredFilters = Object.entries(category?.filters)
    ?.map(([title, values]) => ({
      title,
      ...(values?.buckets?.at(0)?.code
        ? { code: values?.buckets?.at(0)?.code }
        : {}),
      ...values,
    }))
    .filter((f) => f?.buckets?.length);
  category.filters = filteredFilters;

  // Only the fields head/GTM read; omit children_data so __NEXT_DATA__ stays small.
  const node = getCategoryData(treeRes?.data?.data?.children_data, "id", id);
  const cat = node?.id
    ? {
        id: node.id,
        name: node.name ?? null,
        meta_title: node.meta_title ?? null,
        meta_description: node.meta_description ?? null,
        banners: node.banners ?? null,
        category_desktop_image1: node.category_desktop_image1 ?? null,
        category_text_description: node.category_text_description ?? null,
        category_icon: node.category_icon ?? null,
      }
    : null;

  return {
    props: {
      device: isMobile ? "mobile" : "desktop",
      id: id,
      data: category || {},
      cat,
      ...(await serverSideTranslations(locale, ["common", "category", "extended_warranty"]))
    },
  };
}
